import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import type {
	NavItemConfig,
	ObjectListFlyoutAction,
	ObjectListFlyoutItemConfig,
} from '@atlassian/navigation-common/types';

import type { ForYouConfig } from '../../../common/forYouTypes';
import { omitSafely } from '../../../common/utils/omit-safely';
import { STARRED_TEST_ID } from './constants';
import { messages } from './messages';

const elemBefore = <StarUnstarredIcon label="" spacing="spacious" />;

export function useStarredNavItem(forYou: ForYouConfig): [string, NavItemConfig][] {
	const { formatMessage } = useIntl(); // formatMessage() is not a stable function
	const starredConfig = forYou?.starred;
	const viewAllStarredItemsLabel = formatMessage(messages.viewAllStarredItemsLabel);
	const label = formatMessage(messages.label);
	const searchPlaceholder = formatMessage(messages.searchPlaceholder);

	return useMemo(() => {
		if (!starredConfig) {
			return [];
		} else {
			const actions: ObjectListFlyoutAction[] | undefined = starredConfig.viewAllUrl
				? [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: viewAllStarredItemsLabel,
							href: starredConfig.viewAllUrl,
							onClick: starredConfig.viewAllOnClick,
						},
					]
				: starredConfig.viewAllOnClick && [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: viewAllStarredItemsLabel,
							onClick: starredConfig.viewAllOnClick,
						},
					];

			// Omit the type-props from FlyoutProps that are not used by ObjectListFlyout
			const safeConfig = omitSafely(starredConfig, 'menuItemId', 'viewAllOnClick', 'viewAllUrl');

			const value: ObjectListFlyoutItemConfig = {
				...safeConfig,
				actions,
				elemBefore,
				label,
				searchPlaceholder,
				testId: STARRED_TEST_ID,
				type: 'object-list-flyout',
			};

			return [[starredConfig.menuItemId ?? 'global.sidebar.starred', value]];
		}
	}, [label, searchPlaceholder, viewAllStarredItemsLabel, starredConfig]);
}
